import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { SSCoinIcon } from './SSCoin';
import SearchAutocomplete from './SearchAutocomplete';
import { Menu, Globe, Bell, User, LogIn, Plus, ShoppingCart } from 'lucide-react';

export default function Topbar({ onMenuClick }) {
  const { t, lang, toggleLanguage, isRTL } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="lg:sticky lg:top-0 z-50 border-b border-border bg-card/95 backdrop-blur-md">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 gap-4">
        {/* Left: Menu + Search */}
        <div className="flex items-center gap-3 flex-1">
          {/* Mobile: Animated "Start Selling" button */}
          <div className="lg:hidden relative">
            <button 
              className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white font-bold text-xs shadow-lg overflow-hidden whitespace-nowrap animate-[btnPulse_4s_ease-in-out_infinite]" 
              onClick={onMenuClick}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-4 h-4 relative z-10" />
              <span className="relative z-10">{isRTL ? 'ابدأ البيع' : 'Start Selling'}</span>
            </button>
            {/* 3D Mascot Character Animation */}
            <div className="absolute pointer-events-none" style={{bottom:'-95px', right:'-10px', animation:'mascot 12s ease-in-out infinite', zIndex:60}}>
              {/* Smoke/Glow trail */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full" style={{background:'radial-gradient(circle, rgba(45,212,191,0.4) 0%, transparent 70%)', animation:'smoke 12s ease-in-out infinite', filter:'blur(8px)'}} />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full" style={{background:'radial-gradient(circle, rgba(245,158,11,0.3) 0%, transparent 70%)', animation:'smoke 12s ease-in-out 0.2s infinite', filter:'blur(6px)'}} />
              {/* 3D Character Face */}
              <div style={{animation:'mascotFace 12s ease-in-out infinite', transformStyle:'preserve-3d', perspective:'200px'}}>
                <div className="relative w-16 h-16 rounded-full flex items-center justify-center" style={{background:'linear-gradient(135deg, #FFD93D 0%, #FFB800 40%, #FF8C00 100%)', boxShadow:'0 8px 32px rgba(255,184,0,0.5), inset 0 -4px 12px rgba(0,0,0,0.15), inset 0 4px 8px rgba(255,255,255,0.4)', transform:'rotateY(0deg)'}}>
                  {/* Eyes */}
                  <div className="absolute flex gap-2.5" style={{top:'16px'}}>
                    <div className="relative">
                      <div className="w-3.5 h-4 bg-white rounded-full" style={{boxShadow:'inset 0 1px 3px rgba(0,0,0,0.1)'}}>
                        <div className="absolute bottom-1 left-1 w-2 h-2 bg-[#2d1b00] rounded-full" style={{animation:'eyeLook 12s ease-in-out infinite'}}>
                          <div className="absolute top-0 right-0 w-1 h-1 bg-white rounded-full" />
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <div className="w-3.5 h-4 bg-white rounded-full" style={{boxShadow:'inset 0 1px 3px rgba(0,0,0,0.1)'}}>
                        <div className="absolute bottom-1 left-1 w-2 h-2 bg-[#2d1b00] rounded-full" style={{animation:'eyeLook 12s ease-in-out infinite'}}>
                          <div className="absolute top-0 right-0 w-1 h-1 bg-white rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Smile */}
                  <div className="absolute" style={{bottom:'10px', width:'14px', height:'8px', borderRadius:'0 0 10px 10px', background:'linear-gradient(180deg, #e85d04, #d00000)', boxShadow:'inset 0 2px 4px rgba(0,0,0,0.2)', animation:'smile 12s ease-in-out infinite'}} />
                  {/* Cheeks */}
                  <div className="absolute w-2.5 h-1.5 rounded-full bg-[#FF6B6B]/40" style={{bottom:'14px', left:'6px'}} />
                  <div className="absolute w-2.5 h-1.5 rounded-full bg-[#FF6B6B]/40" style={{bottom:'14px', right:'6px'}} />
                  {/* Shine */}
                  <div className="absolute w-5 h-3 rounded-full bg-white/30" style={{top:'6px', left:'8px', transform:'rotate(-20deg)'}} />
                </div>
              </div>
              {/* Waving open hand pointing up at button */}
              <div className="absolute" style={{top:'-22px', left:'2px', animation:'pointHand 12s ease-in-out infinite', transformOrigin:'bottom center'}}>
                <svg width="32" height="36" viewBox="0 0 32 36" fill="none" style={{transform:'rotate(30deg)', filter:'drop-shadow(0 3px 6px rgba(0,0,0,0.2))'}}>
                  <rect x="8" y="14" width="16" height="16" rx="4" fill="url(#handGrad)"/>
                  <rect x="10" y="2" width="4" height="16" rx="2" fill="url(#handGrad)"/>
                  <rect x="15" y="0" width="4" height="16" rx="2" fill="url(#handGrad)"/>
                  <rect x="20" y="3" width="4" height="14" rx="2" fill="url(#handGrad)"/>
                  <rect x="5" y="6" width="4" height="14" rx="2" fill="url(#handGrad)" transform="rotate(-15 5 6)"/>
                  <defs><linearGradient id="handGrad" x1="0" y1="0" x2="32" y2="36"><stop stopColor="#FFD93D"/><stop offset="1" stopColor="#FFB800"/></linearGradient></defs>
                </svg>
              </div>
            </div>
          </div>

          <SearchAutocomplete isAuthenticated={isAuthenticated} />
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Language Toggle - icon only on mobile */}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={toggleLanguage}
            className="text-muted-foreground hover:text-foreground w-8 h-8 sm:w-auto sm:h-auto sm:gap-2 sm:px-3"
            data-testid="language-toggle"
          >
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline text-xs font-medium">{lang === 'ar' ? 'EN' : 'عربي'}</span>
          </Button>

          {/* Coin Balance - compact */}
          {isAuthenticated && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/buy-coins')}
              className="gap-1 bg-gradient-to-r from-amber-500/15 to-yellow-500/10 hover:from-amber-500/25 hover:to-yellow-500/20 border border-amber-500/25 rounded-xl px-2 sm:px-3 h-8 shadow-[0_0_12px_rgba(234,179,8,0.1)]"
              data-testid="topbar-coin-balance"
            >
              <SSCoinIcon size={16} />
              <span className="font-mono text-xs font-bold text-amber-400">{(user?.coin_balance || 0).toLocaleString()}</span>
              <Plus className="w-3 h-3 text-amber-400" />
            </Button>
          )}

          {/* Cart */}
          <CartBadge navigate={navigate} />

          {/* Notifications - hidden on small mobile */}
          {isAuthenticated && (
            <Button 
              variant="ghost" 
              size="icon"
              className="relative text-muted-foreground hover:text-foreground w-8 h-8 hidden xs:flex"
              data-testid="topbar-notifications-button"
            >
              <Bell className="w-4 h-4" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
            </Button>
          )}

          {/* Profile */}
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="flex-shrink-0 w-8 h-8"
                  data-testid="topbar-profile-menu"
                >
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center ring-1.5 ring-primary/30">
                    <span className="text-[10px] font-bold text-primary">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-48">
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                  {t('dashboard')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  {t('settings')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              variant="default" 
              size="sm"
              onClick={() => navigate('/login')}
              className="gap-1.5 flex-shrink-0"
              data-testid="login-button"
            >
              <LogIn className="w-4 h-4" />
              <span className="text-xs">{isRTL ? 'دخول' : 'Login'}</span>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

function CartBadge({ navigate }) {
  const [count, setCount] = React.useState(0);
  const [bounce, setBounce] = React.useState(false);
  React.useEffect(() => {
    const check = () => {
      try {
        const c = JSON.parse(localStorage.getItem('ss_cart') || '[]');
        const newCount = c.reduce((s, i) => s + (i.qty || 0), 0);
        if (newCount > count && count > 0) { setBounce(true); setTimeout(() => setBounce(false), 700); }
        setCount(newCount);
      } catch {}
    };
    check();
    const onStorage = () => check();
    const onCartUpdate = () => check();
    window.addEventListener('storage', onStorage);
    window.addEventListener('cartUpdated', onCartUpdate);
    const interval = setInterval(check, 2000);
    return () => { clearInterval(interval); window.removeEventListener('storage', onStorage); window.removeEventListener('cartUpdated', onCartUpdate); };
  }, [count]);
  if (count === 0) return null;
  return (
    <button onClick={() => navigate('/cart')}
      className={`relative flex items-center gap-1.5 px-3 py-2 rounded-xl border transition-all ${bounce ? 'bg-primary text-white border-primary scale-125 shadow-xl shadow-primary/40' : 'bg-primary/10 hover:bg-primary/20 border-primary/20'}`}
      style={{ transition: 'all 0.3s ease' }}
      data-testid="topbar-cart">
      <ShoppingCart className={`w-4 h-4 ${bounce ? 'text-white' : 'text-primary'}`} />
      <span className={`font-mono text-sm font-bold ${bounce ? 'text-white' : 'text-primary'}`}>{count}</span>
      {bounce && <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />}
    </button>
  );
}

